import { useDroppable } from '@dnd-kit/core';
import { rectSortingStrategy, SortableContext } from '@dnd-kit/sortable';
import { Button, cn, EmptyState } from '@aibi/ui';
import { LayoutDashboard, Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import type { DropTarget } from '../../model/drop-rules';
import { ROW_UNIT_REM } from '../../model/workspace-document';
import { useSelectedCard } from '../../state/use-selected-card';
import { useUiState } from '../../state/ui-state';
import { useWorkspace, useWorkspaceActions } from '../../state/workspace-context';
import { dropFeedbackClass, useActiveDrag, useDropValidity } from '../dnd/drag-state';
import { SelectionList } from '../field-explorer/SelectionList';
import type { AlignmentGuide } from './card-resize';
import { useCardDragSession } from './card-drag-session';
import { CardSnapSlot } from './CardDragOverlay';
import { CardResizeSessionProvider } from './CardResizeSessionProvider';
import { ResizeGuides } from './ResizeGuides';
import { VisualizationCard } from './VisualizationCard';

const CANVAS_TARGET: DropTarget = { type: 'canvas' };

/**
 * Canvas central: rejilla de 12 columnas con colocacion libre (x/y). El area libre es
 * zona de destino: soltar un campo crea una visualizacion; arrastrar una tarjeta la mueve.
 */
export function AnalysisCanvas() {
  const { audience, document } = useWorkspace();
  const { execute } = useWorkspaceActions();
  const { maximizedCardId } = useUiState();
  const selectedCard = useSelectedCard();
  const activeDrag = useActiveDrag();
  const cardDrag = useCardDragSession();
  const { setNodeRef, isOver } = useDroppable({ id: 'canvas', data: { target: CANVAS_TARGET } });
  const valid = useDropValidity(CANVAS_TARGET);
  const maximized = document.cards.find((card) => card.id === maximizedCardId);
  const selections = (selectedCard ?? document.cards[0])?.selections ?? [];
  const showNewTile = activeDrag?.type === 'field';
  const [guides, setGuides] = useState<readonly AlignmentGuide[]>([]);
  const resizeSession = useMemo(() => ({ setGuides }), []);
  const draggingCard = cardDrag
    ? document.cards.find((card) => card.id === cardDrag.cardId)
    : undefined;
  const snap = cardDrag?.snap ?? null;
  const snapSlot =
    snap !== null &&
    draggingCard !== undefined &&
    (snap.x !== (draggingCard.layout.position?.x ?? 0) ||
      snap.y !== (draggingCard.layout.position?.y ?? 0))
      ? snap
      : null;

  return (
    <main aria-label="Lienzo de analisis" className="flex min-w-0 flex-1 flex-col bg-canvas">
      {audience === 'editor' && selections.length > 0 && (
        <div className="border-b border-border bg-surface px-4 py-1.5">
          <SelectionList selections={selections} layout="bar" />
        </div>
      )}
      <div
        ref={setNodeRef}
        className={cn(
          'relative flex-1 overflow-auto p-4',
          isOver && valid === true && 'bg-primary/5',
        )}
      >
        <CardResizeSessionProvider value={resizeSession}>
          {maximized ? (
            <div className="h-full">
              <VisualizationCard card={maximized} maximized />
            </div>
          ) : document.cards.length === 0 ? (
            <EmptyState
              icon={<LayoutDashboard />}
              title="El lienzo esta vacio"
              description="Arrastre un campo aqui para crear su primera visualizacion."
              action={
                <Button
                  size="sm"
                  onClick={() => {
                    execute({ type: 'add-card' });
                  }}
                >
                  <Plus /> Nueva visualizacion
                </Button>
              }
            />
          ) : (
            <SortableContext
              items={document.cards.map((card) => `card:${card.id}`)}
              strategy={rectSortingStrategy}
            >
              <div className="relative">
                <ResizeGuides guides={guides} />
                <div
                  data-canvas-grid
                  className="grid grid-cols-12 gap-3"
                  style={{ gridAutoRows: `${String(ROW_UNIT_REM)}rem` }}
                >
                  {document.cards.map((card) => (
                    <VisualizationCard key={card.id} card={card} />
                  ))}
                  {snapSlot ? <CardSnapSlot snap={snapSlot} /> : null}
                  {showNewTile && (
                    <div
                      aria-hidden
                      className={cn(
                        'col-span-12 flex h-24 items-center justify-center rounded-xl border-2 border-dashed border-border text-sm text-muted-foreground md:col-span-4',
                        dropFeedbackClass(valid, isOver),
                      )}
                    >
                      Suelte aqui para crear una visualizacion nueva
                    </div>
                  )}
                </div>
              </div>
            </SortableContext>
          )}
        </CardResizeSessionProvider>
      </div>
    </main>
  );
}
